import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { Input } from '../../../components/items/joinInput';
import { formatNumber } from '../../../utils/util';
import { Button } from '../../../components/items/Button';
import { CommonTitle } from '../../../components/items/common.title';
import { fetchDriverAmount, updateDriverAmount } from '../../../api/mypage';
import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { driverAmountSchema } from '../../../schema/mypageSchema';
import { AlertModal } from '../../../components/modal/alert.modal';
import { useHistory } from 'react-router';

interface DriverAmountState {
  halfDay: number;
  fullDay: number;
  ryubei: number;
  cycle: number;
  freshWater: number;
  wasteWater: number;
  delay: number;
}

const UserDefaultCostSetting = () => {
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적

  const { handleSubmit, control, setValue } = useForm<DriverAmountState>({
    resolver: zodResolver(driverAmountSchema),
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchDriverAmount();
        // setValue에서 formatNumber 사용
        setValue('halfDay', Number(data.halfDay));
        setValue('fullDay', Number(data.fullDay));
        setValue('ryubei', Number(data.ryubei));
        setValue('cycle', Number(data.cycle));
        setValue('freshWater', Number(data.freshWater));
        setValue('wasteWater', Number(data.wasteWater));
        setValue('delay', Number(data.delay));
      } catch (error) {
        console.error('데이터를 불러오는 중 오류가 발생했습니다.', error);
      }
    };

    fetchData();
  }, [setValue]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/mypage',
      }); // navigateOnClose가 true일 때만 /mypage로 이동
    }
  };

  const onSubmit = async (data: DriverAmountState) => {
    try {
      const response = await updateDriverAmount(data); // API 함수 호출
      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else {
        setModalMessage('기준 금액 설정에 실패했습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(false);
      }
    } catch (error) {
      console.error('저장 중 오류가 발생했습니다:', error);
    }
  };

  return (
    <Container>
      <CommonTitle title="기준 금액 설정" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="setting-wrap">
          <div className="box-item">
            <div className="title">시간 작업</div>
            <div className="row-50">
              <div className="cost-wrapper">
                <Controller
                  name="halfDay"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      label="반일"
                      name="halfDay"
                      value={value ? formatNumber(Number(value)) : ''}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/[^0-9]/g, '');
                        onChange(rawValue !== '' ? Number(rawValue) : 0); // Set value as number
                      }}
                      placeholder="금액입력"
                    />
                  )}
                />
              </div>
              <div className="cost-wrapper">
                <Controller
                  name="fullDay"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      label="전일"
                      name="fullDay"
                      value={value ? formatNumber(Number(value)) : ''}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/[^0-9]/g, '');
                        onChange(rawValue !== '' ? Number(rawValue) : 0); // Set value as number
                      }}
                      placeholder="금액입력"
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className="box-item">
            <div className="title">기준 작업</div>
            <div className="row-50">
              <div className="cost-wrapper">
                <Controller
                  name="ryubei"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      label="루베 금액"
                      name="ryubei"
                      value={value ? formatNumber(Number(value)) : ''}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/[^0-9]/g, '');
                        onChange(rawValue !== '' ? Number(rawValue) : 0); // Set value as number
                      }}
                      placeholder="금액입력"
                    />
                  )}
                />
              </div>
              <div className="cost-wrapper">
                <Controller
                  name="cycle"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      label="회전 금액"
                      name="cycle"
                      value={value ? formatNumber(Number(value)) : ''}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/[^0-9]/g, '');
                        onChange(rawValue !== '' ? Number(rawValue) : 0); // Set value as number
                      }}
                      placeholder="금액입력"
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className="box-item">
            <div className="title">작업 형태</div>
            <div className="row-50">
              <div className="cost-wrapper">
                <Controller
                  name="freshWater"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      label="청수 금액"
                      name="freshWater"
                      value={value ? formatNumber(Number(value)) : ''}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/[^0-9]/g, '');
                        onChange(rawValue !== '' ? Number(rawValue) : 0); // Set value as number
                      }}
                      placeholder="금액입력"
                    />
                  )}
                />
              </div>
              <div className="cost-wrapper">
                <Controller
                  name="wasteWater"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      label="폐수 금액"
                      name="wasteWater"
                      value={value ? formatNumber(Number(value)) : ''}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/[^0-9]/g, '');
                        onChange(rawValue !== '' ? Number(rawValue) : 0); // Set value as number
                      }}
                      placeholder="금액입력"
                    />
                  )}
                />
              </div>
              <div className="cost-wrapper">
                <Controller
                  name="delay"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      label="지연 금액"
                      name="delay"
                      value={value ? formatNumber(Number(value)) : ''}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/[^0-9]/g, '');
                        onChange(rawValue !== '' ? Number(rawValue) : 0); // Set value as number
                      }}
                      placeholder="금액입력"
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>
        <Button title="저 장" type="submit" fiexd />
      </form>
      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

export default UserDefaultCostSetting;

// style
const Container = styled.div`
  padding: 2.4rem 2rem;
  form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    .setting-wrap {
      display: flex;
      flex-direction: column;
      gap: 1.2rem;
      height: calc(100% - 16rem);
      .title {
        font-size: 1.5rem;
        font-weight: 600;
        display: flex;
        align-items: flex-start;
        margin-bottom: 0.8rem;
        span {
          color: #ff5151;
          margin-left: 0.2rem;
          transform: translateY(-3px);
        }
      }
      .row-50 {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.9rem;
      }
    }
  }
`;
